import React from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import type { AlertProps } from "@mui/material/Alert";
import type { BaseMoleculeProps } from "../../types";

export interface ErrorContainerProps extends BaseMoleculeProps {
  /**
   * Error object or error message
   */
  error?: Error | string | null;

  /**
   * Custom error message to display
   */
  message?: string;

  /**
   * Error severity level
   */
  severity?: "error" | "warning" | "info";

  /**
   * Whether to show retry button
   */
  showRetry?: boolean;

  /**
   * Retry button handler
   */
  onRetry?: () => void;

  /**
   * Retry button text
   */
  retryText?: string;

  /**
   * Whether to show error details
   */
  showDetails?: boolean;

  /**
   * Additional Alert props
   */
  alertProps?: Omit<AlertProps, "severity">;
}

const ErrorContainerComponent: React.FC<ErrorContainerProps> = ({
  error,
  message,
  severity = "error",
  showRetry = false,
  onRetry,
  retryText = "Повторить",
  showDetails = false,
  variant = "default",
  className,
  "data-testid": dataTestId,
  alertProps,
  children,
}) => {
  // Don't render if no error
  if (!error && !message) {
    return <>{children}</>;
  }

  // Extract error message
  const errorMessage =
    message ||
    (typeof error === "string" ? error : error?.message) ||
    "Произошла ошибка";
  const errorDetails = typeof error === "object" && error?.stack;

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      padding={2}
      className={className}
      data-testid={dataTestId}
      sx={{
        // Mobile-first responsive styling
        gap: {
          xs: 2, // Mobile spacing
          md: 3, // Desktop spacing
        },
        // Variant-specific styling
        ...(variant === "compact" && {
          padding: 1,
        }),
        ...(variant === "expanded" && {
          padding: 4,
          minHeight: "300px",
        }),
      }}
    >
      <Alert
        severity={severity}
        sx={{
          width: "100%",
          maxWidth: {
            xs: "100%", // Mobile - full width
            md: "600px", // Desktop - constrained width
          },
          // Mobile-first responsive styling
          "& .MuiAlert-message": {
            fontSize: {
              xs: "0.875rem", // Mobile
              md: "1rem", // Desktop
            },
          },
        }}
        {...alertProps}
      >
        <AlertTitle>
          {severity === "error"
            ? "Ошибка"
            : severity === "warning"
              ? "Предупреждение"
              : "Информация"}
        </AlertTitle>

        <Typography
          variant="body2"
          sx={{
            marginBottom: showDetails || showRetry ? 2 : 0,
          }}
        >
          {errorMessage}
        </Typography>

        {showDetails && errorDetails && (
          <Box
            component="details"
            sx={{
              marginTop: 1,
              padding: 1,
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              borderRadius: 1,
              fontSize: "0.75rem",
              fontFamily: "monospace",
              overflow: "auto",
              maxHeight: "200px",
            }}
          >
            <summary style={{ cursor: "pointer", marginBottom: "8px" }}>
              Подробности ошибки
            </summary>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {errorDetails}
            </pre>
          </Box>
        )}

        {showRetry && onRetry && (
          <Box
            sx={{
              marginTop: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              color={severity === "error" ? "error" : "primary"}
              onClick={onRetry}
              size="small"
              sx={{
                // Mobile-first responsive button
                minHeight: "44px", // WCAG AA touch target
                fontSize: {
                  xs: "0.875rem", // Mobile
                  md: "1rem", // Desktop
                },
                padding: {
                  xs: "12px 24px", // Mobile - larger touch targets
                  md: "8px 16px", // Desktop - standard padding
                },
              }}
            >
              {retryText}
            </Button>
          </Box>
        )}
      </Alert>
    </Box>
  );
};

// Apply memoization with React.memo
export const ErrorContainer = React.memo(ErrorContainerComponent);

// Set display name for debugging
(ErrorContainer as any).displayName = "ErrorContainer";
